export const ANALYTICS_CONSENT_KEY = "cookie-consent"
export const ANALYTICS_CONSENT_UPDATED_EVENT = "cookie-consent-updated"

export type ConsentStorage = Pick<Storage, "getItem">

export interface PostHogConsentApi {
  __loaded?: boolean
  has_opted_out_capturing?: () => boolean
  opt_in_capturing?: () => void
  opt_out_capturing?: () => void
}

const DENIED_STORAGE: ConsentStorage = { getItem: () => null }

/** localStorage when available, otherwise a deny-only stand-in. */
export function browserAnalyticsConsentStorage(): ConsentStorage {
  if (typeof window === "undefined") return DENIED_STORAGE
  try {
    return window.localStorage
  } catch {
    return DENIED_STORAGE
  }
}

/** Missing, malformed, unreadable and non-boolean values all deny analytics. */
export function readAnalyticsConsent(storage: ConsentStorage): boolean {
  try {
    const raw = storage.getItem(ANALYTICS_CONSENT_KEY)
    if (!raw) return false
    const value: unknown = JSON.parse(raw)
    return value !== null && typeof value === "object" && !Array.isArray(value) &&
      (value as { analytics?: unknown }).analytics === true
  } catch {
    return false
  }
}

/** Browser analytics consent. Server rendering and blocked storage both deny. */
export function hasAnalyticsConsent(): boolean {
  return readAnalyticsConsent(browserAnalyticsConsentStorage())
}

/**
 * Bring a ready SDK into line with the stored choice. The loading stub stays
 * untouched because SDK initialisation starts opted out. Queued consent calls
 * can run after the ready callback and override its latest-choice decision.
 */
export function reconcilePostHogConsent(
  posthog: PostHogConsentApi | null | undefined,
  allowed: boolean,
  ready: boolean,
): void {
  if (!posthog) return

  if (!ready) return

  let optedOut: boolean | undefined
  try {
    optedOut = posthog.has_opted_out_capturing?.()
  } catch {
    optedOut = undefined
  }

  if (allowed) {
    if (optedOut !== false) posthog.opt_in_capturing?.()
  } else if (optedOut !== true) {
    posthog.opt_out_capturing?.()
  }
}

/** Consent gate used at the instant an application analytics action runs. */
export function canUsePostHog(
  posthog: PostHogConsentApi | null | undefined,
  storage: ConsentStorage,
): boolean {
  if (!posthog || !readAnalyticsConsent(storage)) return false
  if (posthog.__loaded !== true) return false
  try {
    return posthog.has_opted_out_capturing?.() !== true
  } catch {
    return false
  }
}

type ConsentEventTarget = Pick<EventTarget, "addEventListener" | "removeEventListener">

/** Watch both this tab's custom event and relevant cross-tab storage changes. */
export function subscribeToAnalyticsConsent(
  target: ConsentEventTarget,
  storage: ConsentStorage,
  onChange: (allowed: boolean) => void,
): () => void {
  const sync = () => onChange(readAnalyticsConsent(storage))
  const onCustom = () => sync()
  const onStorage = (event: Event) => {
    const change = event as StorageEvent
    if (change.storageArea !== null && change.storageArea !== storage) return
    if (change.key !== null && change.key !== ANALYTICS_CONSENT_KEY) return
    sync()
  }

  target.addEventListener(ANALYTICS_CONSENT_UPDATED_EVENT, onCustom)
  target.addEventListener("storage", onStorage)
  return () => {
    target.removeEventListener(ANALYTICS_CONSENT_UPDATED_EVENT, onCustom)
    target.removeEventListener("storage", onStorage)
  }
}

/** Drop calls made to the public loading stub instead of replaying them later. */
export function guardLoadingPostHog(posthog: {
  capture?: (...args: any[]) => unknown
  identify?: (...args: any[]) => unknown
  alias?: (...args: any[]) => unknown
  reset?: (...args: any[]) => unknown
  people?: { set?: (...args: any[]) => unknown; set_once?: (...args: any[]) => unknown }
}): void {
  // The real SDK replaces these stub methods when it becomes ready. Keeping
  // application actions out of the loading queue avoids consent-order races.
  const blocked = () => undefined
  for (const method of ["capture", "identify", "alias", "reset"] as const) {
    if (typeof posthog[method] === "function") posthog[method] = blocked
  }
  if (posthog.people) {
    if (typeof posthog.people.set === "function") posthog.people.set = blocked
    if (typeof posthog.people.set_once === "function") posthog.people.set_once = blocked
  }
}
